import { getDocs, query, where, orderBy, documentId, QuerySnapshot, DocumentData } from "firebase/firestore/lite";
import { getAuth } from "firebase/auth";
import { projectsSchemeDB, updateRecord } from "../../libs/firebase";
import { generateID } from "ui";
import { ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";


export async function getProjectsScheme(uid: string, sharedProjectIds: string[] = []): Promise<ProjectSchemeInterface[]> {
  if (!projectsSchemeDB) return [];
  if (!uid) throw new Error("User ID not provided");
  
  try {
    // Get owned projects
    const ownedProjectsQuery = query(
      projectsSchemeDB,
      where("owner", "==", uid),
      orderBy("updatedAt", "desc")
    );
    const ownedProjectsSnapshot = await getDocs(ownedProjectsQuery);
    const ownedProjects = ownedProjectsSnapshot.docs.map(doc => doc.data() as ProjectSchemeInterface);
    
    // Get shared projects if any IDs provided
    let sharedProjects: ProjectSchemeInterface[] = [];
    if (sharedProjectIds.length > 0) {
      // Firestore has a limit for 'in' queries (10 items), so we need to batch
      const batches:Promise<QuerySnapshot<DocumentData, DocumentData>>[] = [];
      for (let i = 0; i < sharedProjectIds.length; i += 10) {
        const batch = sharedProjectIds.slice(i, i + 10);
        const sharedProjectsQuery = query(
          projectsSchemeDB,
          where(documentId(), "in", batch),
          orderBy("updatedAt", "desc")
        );
        batches.push(getDocs(sharedProjectsQuery) as Promise<QuerySnapshot<DocumentData, DocumentData>>);
      }
      
      const results: QuerySnapshot<DocumentData>[] = await Promise.all(batches);
      sharedProjects = results.flatMap(snapshot => 
        snapshot.docs.map(doc => doc.data() as ProjectSchemeInterface)
      );
    }
    
    // Combine and sort all projects
    const allProjects = [...ownedProjects, ...sharedProjects];
    
    // Remove duplicates (in case user is both owner and has permissions)
    const uniqueProjects = Array.from(
      new Map(allProjects.map(project => [project.id, project])).values()
    );
 
    // Sort by updatedAt
    return uniqueProjects.sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (error) {
    console.error('Error fetching project schemas:', error);
    return [];
  }
}
/**
 * Create a new project.
 */
export async function createProjectScheme(project: Omit<ProjectSchemeInterface, "id" | "createdAt" | "updatedAt">): Promise<ProjectSchemeInterface> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const newProject = {
    ...project,
    id: generateID(),
    owner: user.uid,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  await updateRecord(newProject.id, "projectsScheme", newProject);
  return newProject;
}

// Helper function to check if user has access to a project
export const hasProjectAccess = (projectId: string,userId: string,permissions: Permissions | null,project?: ProjectSchemeInterface | null): boolean => {
    return hasAccessToResource(permissions, projectId, 'read', 'project', project?.owner || '', userId);
};

export async function getProjectSchemeById(projectId: string): Promise<ProjectSchemeInterface | null> {
  if (!projectsSchemeDB) return null;
  if (!projectId) throw new Error("Project ID not provided");

  const projectQuery = query(projectsSchemeDB,where("id", "==", projectId));
  const projectSnapshot = await getDocs(projectQuery);
  if (projectSnapshot.empty) return null;

  const project = projectSnapshot.docs[0].data() as ProjectSchemeInterface;
  return project;
}

export async function getAssociatedProjects(projectId: string): Promise<ProjectSchemeInterface[]> {
  if (!projectsSchemeDB) return [];

  return [];
}

/**
 * Edit an existing project.
 */
export async function editProjectScheme(project: ProjectSchemeInterface): Promise<ProjectSchemeInterface> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  // filtering out "boards" and "collections" from the project object
  const { boards, collections, ...projectToSave } = project;

  const newProject = {
    ...projectToSave,
    updatedAt: Date.now(),
  };

  try {
    await updateRecord(newProject.id, "projectsScheme", newProject); // Corrected the variable name
  } catch (error) {
    console.error("Error updating project:", error); // Log the error
    throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
  }
  return {
    ...project,
    updatedAt: newProject.updatedAt,
  };
}

/**
 * Duplicate a project.
 */
export async function duplicateProjectScheme(project: ProjectSchemeInterface): Promise<ProjectSchemeInterface> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  // Create a copy of the project, but remove "collections" and "boards" fields if they exist.
  // This is done using object destructuring to omit those fields.
  const { collections, boards, ...rest } = project; // Remove collections and boards if present

  const duplicatedProject = {
    ...rest, // Spread the rest of the project fields (without collections and boards)
    name: `Copy of ${project.name}`, 
    id: generateID(),               
    owner: user.uid,                 // Set the current user as the owner
    createdAt: Date.now(),           
    updatedAt: Date.now(),           
    // collections and boards are intentionally omitted
  };

  try {
    await updateRecord(duplicatedProject.id, "projectsScheme", duplicatedProject);
  } catch (error) {
    console.error("Error updating project:", error); // Log the error
    throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
  }
  return duplicatedProject;
}

/**
 * Delete a project.
 */
export async function deleteProjectScheme(project: ProjectSchemeInterface): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  // Check if the user is the owner or an editor
  const isOwner = project.owner === user.uid;
  if (!isOwner) {
    throw new Error("You do not have permission to delete this project");
  }

  const updatedData = {
    archive: true,
    updatedAt: Date.now()
  };

  try {
    await updateRecord(project.id, "projectsScheme", updatedData);
  } catch (error) {
    console.error("Error updating project:", error); // Log the error
    throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
  }

}

// export async function getProjectSchemeThumbnails(projectId: string): Promise<{ id: string; screenshot: string; updatedAt: number; }[]> {
//   const auth = getAuth();
//   const user = auth.currentUser;
//   if (!user) throw new Error("User not authenticated");

//   if (!projectsDB) return [];

//   const q = query(
//     projectsDB,
//     where('associatedProjectId', '==', projectId),
//     orderBy('updatedAt', 'desc'),
//     limit(3),
//   );
//   const snapshot = await getDocs(q);
//   const projectThumbnailArray = snapshot.docs.map(doc => {
//     const data:any = doc.data();
//       return {
//         id: doc.id,
//         screenshot: data.files?.screenshot,
//         updatedAt: data.updatedAt,
//       };
//   });
//   return projectThumbnailArray;
// }

// export async function getThumbnailsForProjectsScheme(projectIds: string[]): Promise<Record<string, string[]>> {
//   const auth = getAuth();
//   const user = auth.currentUser;
//   if (!user) throw new Error("User not authenticated");

//   if (!projectsDB) return {};

//   const projectThumbnailArray: Record<string, string[]> = {};

//   for (const projectId of projectIds) {
//     const thumbnails = await getProjectSchemeThumbnails(projectId);
//     projectThumbnailArray[projectId] = thumbnails.map(thumb => thumb.screenshot).filter(Boolean);
//   }

//   return projectThumbnailArray;
// }

export async function getAssociatedProjectsForUser(uid: string): Promise<ProjectSchemeInterface[]> {
  if (!projectsSchemeDB) return [];
  if (!uid) throw new Error("User ID not provided");

  const projectsQuery = query(
    projectsSchemeDB,
    where("owner", "==", uid),
    orderBy("updatedAt", "desc")
  );
  const projectsSnapshot = await getDocs(projectsQuery);
  const projects = projectsSnapshot.docs.map(doc => (doc.data() as ProjectSchemeInterface));
  return projects;
}